/**
 * 统一返回 / 回首页兜底
 *
 * 背景：uni-app 的 navigateBack 会先把 delta 钳制为 `getCurrentPages().length - 1`
 * （见 uni-h5 的 NavigateBackOptions.formatArgs.delta），
 * 当页面栈只有一个页面时（扫码、分享链接等深层链接直接进入内页），delta 会变成 0，
 * H5 端 navigateBack 内部执行 `$router.go(-0)` 等价于 `history.go(0)`，即刷新当前页，
 * 表现为「点返回回不到首页，一直在当前页刷新」。
 *
 * uni-app 自带原生导航栏的返回按钮有 `getCurrentPages().length === 1 -> reLaunch('/')` 的兜底，
 * 但项目使用的是自定义导航栏（wd-navbar 等），直接调 uni.navigateBack 会绕过该兜底，因此需要这里补齐。
 */
import { useAppStore } from '@/store/modules/app';
import { tabbarList } from '@/tabbar/store';
import { getLastPage, HOME_PAGE } from '@/utils';
import { isNeedLoginMode, LOGIN_PAGE } from './config';

/** tabbar 首页路径（取首个 tabbar 项）：工作台包为工作台页，其余包为 home 页 */
export const HOME_TABBAR_PAGE = tabbarList.value[0]?.pagePath || HOME_PAGE;

/**
 * 回到首页（兜底用）
 * 项目配置尚未加载完成时先回启动页，由启动页等配置就绪后再进首页，避免首页数据异常
 * 工作台模式（白名单登录策略）未登录时目标为首 tab 工作台页，
 * 经路由拦截器重定向到登录页，登录成功后按 redirect 回到工作台
 */
export function goHome() {
  const appStore = useAppStore();

  if (!appStore.isConfigLoaded) {
    uni.reLaunch({ url: HOME_PAGE });
    return;
  }

  uni.switchTab({
    url: HOME_TABBAR_PAGE,
    fail: () => {
      uni.reLaunch({ url: HOME_TABBAR_PAGE });
    },
  });
}

/**
 * 统一返回：页面栈不足时回首页兜底
 * @param delta 返回层数，默认 1
 */
export function goBack(delta: number = 1) {
  if (getCurrentPages().length <= 1) {
    goHome();
    return;
  }
  uni.navigateBack({ delta });
}

/**
 * 统一跳登录页（业务组件直接进登录时使用，勿绕过路由拦截器）
 * 自动携带当前页路径作为 redirect，登录成功后回跳来源页；
 * 工作台模式（白名单策略）用 reLaunch 保证页面栈只保留「登录页或 tabbar 页」，
 * 其余模式维持 navigateTo（保留返回栈）
 */
export function goLoginPage() {
  let fullPath = '';
  const last = getLastPage();
  if (last?.route) {
    fullPath = last.route.startsWith('/') ? last.route : `/${last.route}`;
    const query = (last as any).options || {};
    const keys = Object.keys(query);
    if (keys.length > 0) {
      fullPath += `?${keys.map(k => `${k}=${query[k]}`).join('&')}`;
    }
  }
  const url = fullPath ? `${LOGIN_PAGE}?redirect=${encodeURIComponent(fullPath)}` : LOGIN_PAGE;
  if (isNeedLoginMode) {
    uni.reLaunch({ url });
  }
  else {
    uni.navigateTo({ url });
  }
}

/**
 * 退出登录后的统一转接（各退出入口 await logout() 后调用）
 * 工作台模式（白名单策略）：reLaunch 到登录页（页面栈只保留登录页），导航完成后回调；
 * 其余模式：不接管跳转，返回 false，由调用方维持原有行为（如回 mine 页 / navigateBack）
 */
export function afterLogoutRedirect(onNavigated?: () => void): boolean {
  if (!isNeedLoginMode) {
    return false;
  }
  uni.reLaunch({
    url: LOGIN_PAGE,
    complete: () => onNavigated?.(),
  });
  return true;
}
